import {ScreenLayout} from "@/components/layouts/page/ScreenLayout";
import TableLayout from "@/components/layouts/page/TableLayout";
import {setSessions} from "@/redux/slices/commonSlices/viewTabsSlice";

import {useCallback, useEffect, useMemo, useState} from "react";

import {useDeepCompareEffect} from "@hooks/DeepCompareEffect";
import {useLevelOrder} from "@hooks/LevelOrder";
import {useViewTabs} from "@hooks/ViewTabs";
import {usePopup} from "@layouts/popup/InfoPopup/PopupContext";
import {DeleteDataPopup} from "@popups/attentionPopups/DeleteDataPopup";
import {
  clearTable,
  decrementTotalData,
  getCertificationSessions,
  getProductSessions,
  getScenarioSessions,
  setPage,
  setSearch,
  setSelectedData,
  setSize,
  setSort,
  setTotalData,
  setTotalPages,
} from "@redux/slices/sessionSlices/tableSlice";
import {
  deleteCertificationSession,
  deleteProductSession,
  deleteScenarioSession,
} from "@redux/slices/sessionSlices/viewSlice";
import {ControlButton} from "@ui/buttons/ControlButton";
import {TableButton} from "@ui/buttons/TableButton";
import {RootCheckbox} from "@ui/inputs/RootCheckbox";
import moment from "moment/min/moment-with-locales";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {useLocation, useNavigate} from "react-router-dom";

export const SessionTablePage = () => {
  const {openPopup, closePopup} = usePopup();
  const {currentLevel} = useLevelOrder();
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const {sessions: sessionTabs} = useSelector((state) => state.common.viewTabs);
  const {demo} = useSelector((state) => state.common.candidate);
  const {SESSIONS_VIEW_TABS} = useViewTabs();

  const {
    data,
    page,
    size,
    sort,
    totalPages,
    status,
    search,
    selectedData,
    totalData,
  } = useSelector((state) => state.session.table);

  const [params, setParams] = useState({page: 1, size, sort});

  const handleClickDeleteAll = () => {
    openPopup(
      <DeleteDataPopup
        onClickNo={() => closePopup()}
        onClickYes={() =>
          sessionTabs === "scenario"
            ? deleteAllScenarios()
            : sessionTabs === "product"
              ? deleteAllProducts()
              : deleteAllCertifications()
        }
      />,
      {closeButton: false},
    );
  };

  const deleteAllScenarios = useCallback(async () => {
    try {
      const results = await Promise.all(
        selectedData.map((session) =>
          dispatch(deleteScenarioSession({id: session.id})),
        ),
      );

      if (
        results.every((result) => deleteScenarioSession.fulfilled.match(result))
      ) {
        const newParams = {
          page,
          size,
          ...(search && {search}),
          ...(Object.keys(sort).length !== 0 && {sort}),
        };

        dispatch(setTotalData(totalData - results.length));
        setParams(newParams);
        dispatch(getScenarioSessions(newParams));
        closePopup();
      }
    } catch (e) {
      console.log("Error deleting all scenarios: ", e);
    }
  }, [page, size, search, sort, selectedData, totalData, sessionTabs]);

  const deleteAllProducts = useCallback(async () => {
    try {
      const results = await Promise.all(
        selectedData.map((session) =>
          dispatch(deleteProductSession({id: session.id})),
        ),
      );

      if (
        results.every((result) => deleteProductSession.fulfilled.match(result))
      ) {
        const newParams = {
          page,
          size,
          ...(search && {search}),
          ...(Object.keys(sort).length !== 0 && {sort}),
        };

        dispatch(setTotalData(totalData - results.length));
        setParams(newParams);
        dispatch(getProductSessions(newParams));
        closePopup();
      }
    } catch (e) {
      console.log("Error deleting all products: ", e);
    }
  }, [page, size, search, sort, selectedData, totalData, sessionTabs]);

  const deleteAllCertifications = useCallback(async () => {
    try {
      const results = await Promise.all(
        selectedData.map((session) =>
          dispatch(deleteCertificationSession({id: session.id})),
        ),
      );

      if (
        results.every((result) =>
          deleteCertificationSession.fulfilled.match(result),
        )
      ) {
        const newParams = {
          page,
          size,
          ...(search && {search}),
          ...(Object.keys(sort).length !== 0 && {sort}),
        };

        dispatch(setTotalData(totalData - results.length));
        setParams(newParams);
        dispatch(getCertificationSessions(newParams));
        closePopup();
      }
    } catch (e) {
      console.log("Error deleting all certifications: ", e);
    }
  }, [page, size, search, sort, selectedData, totalData, sessionTabs]);

  const handleSearch = useCallback(
    (value) => {
      clearTimeout(window.searchTimeout);

      window.searchTimeout = setTimeout(() => {
        dispatch(setSearch(value.toLowerCase()));

        if (sessionTabs === "scenario") {
          dispatch(getScenarioSessions({page: 1, size, search: value}));
        } else if (sessionTabs === "product") {
          dispatch(getProductSessions({page: 1, size, search: value}));
        } else if (sessionTabs === "certification") {
          dispatch(getCertificationSessions({page: 1, size, search: value}));
        }
      }, 500);
    },
    [dispatch, size, sessionTabs],
  );

  const goToPage = (pageIndex) => {
    dispatch(setPage(pageIndex + 1));
  };

  const handleSelectSize = (option) => {
    dispatch(setSize(+option));
    dispatch(setPage(1));
    dispatch(setTotalPages(Math.ceil(totalPages / size)));
  };

  const handleSelectSort = (option) => {
    dispatch(setSort(option));
  };

  const deleteScenario = async (id) => {
    try {
      const resultAction = await dispatch(deleteScenarioSession({id}));

      if (deleteScenarioSession.fulfilled.match(resultAction)) {
        const newParams = {
          page,
          size,
          ...(search && {search}),
          ...(Object.keys(sort).length !== 0 && {sort}),
        };

        dispatch(decrementTotalData());
        setParams(newParams);
        dispatch(getScenarioSessions(newParams));
        closePopup();
      }
    } catch (e) {
      console.log("Error delete scenario: ", e);
    }
  };

  const deleteProduct = async (id) => {
    try {
      const resultAction = await dispatch(deleteProductSession({id}));

      if (deleteProductSession.fulfilled.match(resultAction)) {
        const newParams = {
          page,
          size,
          ...(search && {search}),
          ...(Object.keys(sort).length !== 0 && {sort}),
        };

        dispatch(decrementTotalData());
        setParams(newParams);
        dispatch(getProductSessions(newParams));
        closePopup();
      }
    } catch (e) {
      console.log("Error delete product: ", e);
    }
  };

  const deleteCertification = async (id) => {
    try {
      const resultAction = await dispatch(deleteCertificationSession({id}));

      if (deleteCertificationSession.fulfilled.match(resultAction)) {
        const newParams = {
          page,
          size,
          ...(search && {search}),
          ...(Object.keys(sort).length !== 0 && {sort}),
        };

        dispatch(decrementTotalData());
        setParams(newParams);
        dispatch(getCertificationSessions(newParams));
        closePopup();
      }
    } catch (e) {
      console.log("Error delete certification: ", e);
    }
  };

  const handleClickDeleteScenario = (item) => {
    openPopup(
      <DeleteDataPopup
        onClickYes={() => deleteScenario(item.id)}
        onClickNo={() => closePopup()}
      />,
      {closeButton: false},
    );
  };

  const handleClickDeleteProduct = (item) => {
    openPopup(
      <DeleteDataPopup
        onClickYes={() => deleteProduct(item.id)}
        onClickNo={() => closePopup()}
      />,
      {closeButton: false},
    );
  };

  const handleClickDeleteCertification = (item) => {
    openPopup(
      <DeleteDataPopup
        onClickYes={() => deleteCertification(item.id)}
        onClickNo={() => closePopup()}
      />,
      {closeButton: false},
    );
  };

  const controls = [
    {
      id: 0,
      button: (
        <ControlButton
          color={"var(--red)"}
          txt={t("table.button.delete")}
          onClick={() => handleClickDeleteAll()}
          disabled={!selectedData || selectedData.length === 0}
        />
      ),
    },
  ];

  const scenarioColumns = useMemo(() => {
    const baseColumns = [
      {
        Header: t("table.scenario"),
        accessor: "title",
        Cell: ({value, row}) => {
          return (
            <i
              title={
                !row?.original?.scenario ? t("table.deleted_scenario") : value
              }
              style={
                !row?.original?.scenario
                  ? {opacity: "0.5", cursor: "default"}
                  : {opacity: "1", cursor: "default"}
              }
            >
              {value}
            </i>
          );
        },
        width: 225,
      },
      {Header: t("table.user"), accessor: "user", width: 200},
      {
        Header: t("table.products"),
        accessor: "scenario_products",
        Cell: ({value}) => {
          return (
            <>
              {value &&
                value.map((product, index) => (
                  <i
                    title={
                      product.deleted
                        ? t("table.deleted_product")
                        : product.name
                    }
                    key={product.name}
                    style={
                      product.deleted
                        ? {opacity: "0.5", cursor: "default"}
                        : {opacity: "1", cursor: "default"}
                    }
                  >
                    {product.name}
                    {index < value.length - 1 ? ", " : ""}
                  </i>
                ))}
            </>
          );
        },
        width: 220,
      },
      {
        Header: t("table.level"),
        accessor: "level",
        Cell: ({cell: {value}}) => {
          return <>{currentLevel(value)}</>;
        },
        width: 130,
      },
      {
        Header: t("table.result"),
        accessor: "feedback_score",
        Cell: ({cell: _, row}) =>
          `${row?.original?.successful ? t("table.certified") : t("table.not_certified")} / ${Math.round(Number(row?.original?.feedback_score)) || 0}`,
        width: 230,
      },
      {
        Header: t("table.date_time"),
        accessor: "created_at",
        Cell: ({cell: {value}}) =>
          value ? moment(value).format("DD-MM-YYYY HH:mm:ss") : "",
        width: 255,
      },
      {
        Header: t("table.actions"),
        Cell: ({row}) => (
          <div
            style={
              demo
                ? {
                    justifyContent: "flex-end",
                    marginLeft: "auto",
                    width: "fit-content",
                  }
                : {}
            }
            className={"flex gap-[8rem] phone:gap-[40rem]"}
          >
            <TableButton
              icon={"view"}
              color={"var(--orange)"}
              label={t("table.button.view")}
              action={"view"}
              item={row.original}
            />

            {!demo && (
              <TableButton
                icon={"delete"}
                color={"var(--red)"}
                label={t("table.button.delete")}
                action={"delete"}
                onClickAction={() => handleClickDeleteScenario(row.original)}
                item={row.original}
                disabled={!row?.original?.scenario}
              />
            )}
          </div>
        ),
        width: 155,
      },
    ];

    if (!demo) {
      const allSessions = data.every((session) => !session.scenario);

      baseColumns.unshift({
        id: "selection",
        Header: ({getToggleAllRowsSelectedProps}) => (
          <div>
            <RootCheckbox
              disabled={allSessions}
              {...getToggleAllRowsSelectedProps()}
            />
          </div>
        ),
        Cell: ({row}) => (
          <div>
            <RootCheckbox
              disabled={!row?.original?.scenario}
              {...row.getToggleRowSelectedProps()}
            />
          </div>
        ),
        width: 50,
      });
    }

    return baseColumns;
  }, [t, demo, page, size, search, sort, sessionTabs, data]);

  const productColumns = useMemo(() => {
    const baseColumns = [
      {
        Header: t("table.product"),
        accessor: "title",
        Cell: ({value, row}) => {
          return (
            <i
              title={
                !row?.original?.product ? t("table.deleted_product") : value
              }
              style={
                !row?.original?.product
                  ? {opacity: "0.5", cursor: "default"}
                  : {opacity: "1", cursor: "default"}
              }
            >
              {value}
            </i>
          );
        },
        width: 190,
      },
      {Header: t("table.user"), accessor: "user", width: 240},
      {Header: t("table.product_line"), accessor: "product_line", width: 270},
      {
        Header: t("table.level"),
        accessor: "level",
        Cell: ({cell: {value}}) => {
          return <>{currentLevel(value)}</>;
        },
        width: 130,
      },
      {
        Header: t("table.score"),
        accessor: "score",
        Cell: ({row}) => {
          const score = row?.original?.score;
          const maxScore = row?.original?.questions_count;

          return (
            <>{`${Math.round(Number(score)) || 0} / ${Math.round(Number(maxScore)) || 0}`}</>
          );
        },
        width: 175,
      },
      {
        Header: t("table.date_time"),
        accessor: "created_at",
        Cell: ({cell: {value}}) =>
          value ? moment(value).format("DD-MM-YYYY HH:mm:ss") : "",
        width: 255,
      },
      {
        Header: t("table.actions"),
        Cell: ({row}) => (
          <div
            style={
              demo
                ? {
                    justifyContent: "flex-end",
                    marginLeft: "auto",
                    width: "fit-content",
                  }
                : {}
            }
            className={"flex gap-[8rem] phone:gap-[40rem]"}
          >
            <TableButton
              icon={"view"}
              color={"var(--orange)"}
              label={t("table.button.view")}
              action={"view"}
              item={row.original}
            />

            {!demo && (
              <TableButton
                icon={"delete"}
                color={"var(--red)"}
                label={t("table.button.delete")}
                action={"delete"}
                onClickAction={() => handleClickDeleteProduct(row.original)}
                item={row.original}
                disabled={!row?.original?.product}
              />
            )}
          </div>
        ),
        width: 155,
      },
    ];

    if (!demo) {
      const allSessions = data.every((session) => !session.product);

      baseColumns.unshift({
        id: "selection",
        Header: ({getToggleAllRowsSelectedProps}) => (
          <div>
            <RootCheckbox
              disabled={allSessions}
              {...getToggleAllRowsSelectedProps()}
            />
          </div>
        ),
        Cell: ({row}) => (
          <div>
            <RootCheckbox
              disabled={!row?.original?.product}
              {...row.getToggleRowSelectedProps()}
            />
          </div>
        ),
        width: 50,
      });
    }

    return baseColumns;
  }, [t, demo, page, size, search, sort, sessionTabs, data]);

  const certificationColumns = useMemo(() => {
    const baseColumns = [
      {
        Header: t("table.scenario"),
        accessor: "scenario",
        width: 260,
      },
      {Header: t("table.user"), accessor: "user", width: 260},
      {
        Header: t("table.products"),
        accessor: "scenario_products",
        Cell: ({value}) => {
          return (
            <>
              {value &&
                value.map((product, index) => (
                  <i
                    title={
                      product.deleted
                        ? t("table.deleted_product")
                        : product.name
                    }
                    key={product.name}
                    style={
                      product.deleted
                        ? {opacity: "0.5", cursor: "default"}
                        : {opacity: "1", cursor: "default"}
                    }
                  >
                    {product.name}
                    {index < value.length - 1 ? ", " : ""}
                  </i>
                ))}
            </>
          );
        },
        width: 235,
      },
      {
        Header: t("table.result"),
        accessor: "feedback_score",
        Cell: ({cell: _, row}) =>
          `${row?.original?.certified ? t("table.certified") : t("table.not_certified")} / ${Math.round(Number(row?.original?.feedback_score)) || 0}`,
        width: 230,
      },
      {
        Header: t("table.date_time"),
        accessor: "created_at",
        Cell: ({cell: {value}}) =>
          value ? moment(value).format("DD-MM-YYYY HH:mm:ss") : "",
        width: 275,
      },
      {
        Header: t("table.actions"),
        Cell: ({row}) => (
          <div
            style={
              demo
                ? {
                    justifyContent: "flex-end",
                    marginLeft: "auto",
                    width: "fit-content",
                  }
                : {}
            }
            className={"flex gap-[8rem] phone:gap-[40rem]"}
          >
            <TableButton
              icon={"view"}
              color={"var(--orange)"}
              label={t("table.button.view")}
              action={"view"}
              item={row.original}
            />

            {!demo && (
              <TableButton
                icon={"delete"}
                color={"var(--red)"}
                label={t("table.button.delete")}
                action={"delete"}
                onClickAction={() =>
                  handleClickDeleteCertification(row.original)
                }
                item={row.original}
              />
            )}
          </div>
        ),
        width: 155,
      },
    ];

    if (!demo) {
      baseColumns.unshift({
        id: "selection",
        Header: ({getToggleAllRowsSelectedProps}) => (
          <div>
            <RootCheckbox {...getToggleAllRowsSelectedProps()} />
          </div>
        ),
        Cell: ({row}) => (
          <div>
            <RootCheckbox {...row.getToggleRowSelectedProps()} />
          </div>
        ),
        width: 50,
      });
    }

    return baseColumns;
  }, [t, demo, page, size, search, sort, sessionTabs, data]);

  useEffect(() => {
    const newParams = {
      ...(page > 0 ? {page} : {page: 1}),
      size,
      ...(search && {search}),
      ...(Object.keys(sort).length !== 0 && {sort}),
    };

    setParams(newParams);
  }, [page, search, size, sort]);

  useDeepCompareEffect(() => {
    sessionTabs === "certification"
      ? dispatch(getCertificationSessions(params))
      : sessionTabs === "scenario"
        ? dispatch(getScenarioSessions(params))
        : dispatch(getProductSessions(params));
  }, [params, sessionTabs, dispatch]);

  // calculate total pages when totalData changes
  useEffect(() => {
    dispatch(setTotalPages(Math.ceil(totalData / size)));
  }, [totalData]);

  // calculate page when page > totalPages and data is empty
  useEffect(() => {
    if (page === totalPages + 1) {
      dispatch(setPage(Math.ceil(totalData / size)));
    }
  }, [totalPages]);

  useEffect(() => {
    const {pathname} = location;

    pathname.includes("/analytics/certification")
      ? dispatch(setSessions("certification"))
      : pathname.includes("/analytics/scenario")
        ? dispatch(setSessions("scenario"))
        : dispatch(setSessions("product"));
  }, [location.pathname, dispatch]);

  const handleTabChange = useCallback(
    (value) => {
      dispatch(setSessions(value));

      const newPath = `/admin-panel/analytics/${value}`;
      navigate(newPath, {replace: true});

      dispatch(setPage(1));
      setParams({...params, page: 1, search: ""});
      dispatch(clearTable());
    },
    [dispatch, navigate],
  );

  return (
    <ScreenLayout
      controlsList={!demo && controls}
      switchView
      switchWidth={700}
      switchList={SESSIONS_VIEW_TABS}
      switchValue={sessionTabs}
      switchOnChange={handleTabChange}
      title={
        sessionTabs === "certification"
          ? t("page.analytics.certification_title")
          : sessionTabs === "scenario"
            ? t("page.analytics.simulation_title")
            : t("page.analytics.product_title")
      }
    >
      <TableLayout
        serverStatus={status}
        columns={
          sessionTabs === "certification"
            ? certificationColumns
            : sessionTabs === "scenario"
              ? scenarioColumns
              : productColumns
        }
        data={data}
        dataSort={sort}
        selectSort={handleSelectSort}
        dataSize={size}
        selectSize={handleSelectSize}
        searchPlaceholder={t("table.search_sessions")}
        searchValue={search}
        onSearch={handleSearch}
        totalPages={totalPages}
        currentPage={page}
        toPage={goToPage}
        setSelected={(items) => dispatch(setSelectedData(items))}
      />
    </ScreenLayout>
  );
};
